import { z } from 'zod';
/**
 * Roles del sistema y qué puede hacer cada uno.
 *
 * La matriz vive acá, en `packages/schemas`, y no en el backend, por una razón
 * concreta: el frontend tiene que poder decidir qué mostrar con **exactamente**
 * el mismo criterio con el que el backend decide qué permitir. Dos listas
 * paralelas se separan; ésta es una sola.
 *
 * Que el frontend la use para ocultar botones no la convierte en un control de
 * seguridad del lado del cliente: **el que manda es el middleware del backend**.
 * Ocultar es cortesía, rechazar es la garantía.
 *
 * Fuente: `dev/seguridad-identidad.md` §2.
 */
export const rolSchema = z.enum([
    'PACIENTE',
    'CUIDADOR',
    'PROFESIONAL',
    'COORDINADOR',
    'ADMIN_INSTITUCION',
    'ADMIN_FUNDACION',
]);
/**
 * Permisos, nombrados por lo que habilitan y no por el endpoint que los usa.
 *
 * Un permiso describe una capacidad del dominio; los endpoints se agrupan bajo
 * ellos. Así, agregar un endpoint no obliga a tocar la matriz salvo que traiga
 * una capacidad nueva de verdad.
 */
export const permisoSchema = z.enum([
    // Paciente sobre lo suyo
    'checkin:responder',
    'escala:responder',
    'pia:conversar',
    'contenido:ver',
    // Cuidador
    'paciente:acompanar',
    // Equipo clínico
    'paciente:ver',
    'paciente:gestionar',
    /**
     * Dar de alta a un paciente (E3.24). Aparte de `paciente:gestionar` porque lo
     * tienen también la coordinación y la administración de la institución, que
     * no gestionan casos: cargan altas (decisión de Mariano, respuesta 3 de la
     * noche de E3.24). Quien lo tiene ve, en la pantalla de alta, la identidad
     * descifrada de los candidatos; no le abre la lista ni la ficha.
     */
    'paciente:dar_de_alta',
    /**
     * Cerrar el programa (CIERRES-1, M-13). Lo tienen el profesional y la
     * coordinación; qué tipo puede cerrar cada uno lo dice `ROLES_QUE_CIERRAN`
     * (el fallecimiento, sólo el profesional; la pérdida de seguimiento, sólo la
     * coordinación).
     */
    'programa:cerrar',
    'nota:registrar',
    'timeline:ver',
    // Coordinación
    'cohorte:ver',
    'reporte:institucion',
    // Administración
    'usuario:gestionar',
    'tenant:configurar',
    // Fundación
    /**
     * Ver la red (ADMIN-1): el panel A-01 y las instituciones A-02, **sólo con
     * cuentas agregadas**. No abre ninguna fila de paciente, ningún alias ni ningún
     * caso, y no compara instituciones entre sí (`CLAUDE.md` §4.2). Es la lectura
     * anonimizada que `withSuperadmin` ya preveía para Fundación (§10.5).
     */
    'red:ver',
    'corpus:gestionar',
    'sistema:ver',
    'reglas:configurar',
]);
/**
 * La matriz.
 *
 * Cada rol lista **exactamente** lo que puede. No hay herencia entre roles a
 * propósito: un coordinador no es "un profesional con más cosas", y modelarlo
 * así hace que agregar un permiso a profesional se lo regale a media
 * organización sin que nadie lo note. Repetir es más largo y más seguro.
 */
export const MATRIZ_DE_PERMISOS = Object.freeze({
    PACIENTE: ['checkin:responder', 'escala:responder', 'pia:conversar', 'contenido:ver'],
    // Scope acotado por diseño (C-00 a C-05): acompaña, no ve datos clínicos
    // individuales, ni conversaciones con PIA, ni escalas.
    CUIDADOR: ['paciente:acompanar', 'contenido:ver'],
    PROFESIONAL: [
        'paciente:ver',
        'paciente:gestionar',
        'paciente:dar_de_alta',
        'programa:cerrar',
        'nota:registrar',
        'timeline:ver',
    ],
    // Ve la cohorte agregada, no gestiona casos individuales. Sí carga altas.
    COORDINADOR: [
        'paciente:ver',
        'paciente:dar_de_alta',
        'programa:cerrar',
        'timeline:ver',
        'cohorte:ver',
        'reporte:institucion',
    ],
    // Carga altas y no ve pacientes: la lista y la ficha no son de su rol.
    ADMIN_INSTITUCION: [
        'paciente:dar_de_alta',
        'usuario:gestionar',
        'tenant:configurar',
        'cohorte:ver',
        'reporte:institucion',
    ],
    // Cross-tenant y acotado: la red en cuentas agregadas, corpus, configuración
    // global, estado del sistema. NO ve pacientes individuales de ninguna
    // institución, ni agregados de a uno: `red:ver` devuelve cuentas, nunca filas.
    //
    // `reglas:configurar` está **sólo acá**, y es lo que sostiene en código la
    // regla 5 de `dev/semaforos-etiquetas.md` §1.1: las bandas puntaje → color son
    // capa global de Fundación, **no configurables por tenant**. Un admin de
    // institución ve con qué umbrales evalúa el motor y no puede correr ninguno.
    ADMIN_FUNDACION: ['red:ver', 'corpus:gestionar', 'sistema:ver', 'reglas:configurar'],
});
export function rolTienePermiso(rol, permiso) {
    return MATRIZ_DE_PERMISOS[rol].includes(permiso);
}
/** Roles que entran por magic link de WhatsApp, sin contraseña (P-01, C-00/C-01). */
export const ROLES_SIN_CONTRASENA = Object.freeze(['PACIENTE', 'CUIDADOR']);
/**
 * Los roles del equipo: todo rol que **no** entra por magic link. Se deriva de
 * la lista de roles y no se escribe aparte, para que un rol nuevo del equipo
 * quede del lado del equipo por defecto.
 */
export const ROLES_DEL_EQUIPO = Object.freeze(rolSchema.options.filter((rol) => !ROLES_SIN_CONTRASENA.includes(rol)));
/**
 * Roles que exigen segundo factor: **todo el equipo** (decisión de Mariano,
 * 30/09/2026, SEG-1). Hasta entonces eran sólo las dos administraciones, y
 * PROFESIONAL y COORDINADOR, que ven pacientes, entraban sólo con contraseña.
 *
 * Es el mismo conjunto que `ROLES_DEL_EQUIPO`, a propósito: un rol nuevo del
 * equipo nace con segundo factor. Sacarle el segundo factor a un rol exige
 * cambiar esta línea, y un test lo ve (`roles.test.ts`).
 */
export const ROLES_CON_MFA = ROLES_DEL_EQUIPO;
export function entraPorMagicLink(rol) {
    return ROLES_SIN_CONTRASENA.includes(rol);
}
export function exigeMfa(rol) {
    return ROLES_CON_MFA.includes(rol);
}
//# sourceMappingURL=roles.js.map